/**
 * headerFit — the arithmetic behind the header's overflow menu.
 *
 * Pure functions, no DOM and no React, so every width scenario that matters is
 * a table row in `headerFit.test.ts` beside this file rather than something only
 * a browser can see (jsdom computes no layout).
 *
 * Two decisions live here:
 *
 * - `computeFit` splits the facet strip into the items that still fit and the
 *   ones that move into the More disclosure.
 * - `isCrowded` decides whether the account chip should collapse to an avatar,
 *   written so it CANNOT oscillate.
 */

/**
 * Marks the More button's measuring mirror, as a `data-mirror-key` value.
 *
 * It lives in this module rather than in `OverflowNav.tsx` because three places
 * need the same literal — the component that writes the attribute and both
 * suites that stub a rect against it — and a shared literal with no compile-time
 * link is a silent break waiting for whoever edits one of them. No item key can
 * collide with it: `OverflowNav`'s callers key items by facet id.
 */
export const MORE_MIRROR_KEY = '__more__'

export interface FitInput {
  /** Natural (unshrunk) width of each item, in DOM order. */
  itemWidths: number[]
  /** Measured width of the strip. `undefined` = not yet measured. */
  available: number | undefined
  /**
   * Width to reserve for the More button, measured at its widest-label upper
   * bound rather than at the real hidden count — the real label is what gets
   * rendered, and it is never wider than this. See the plan's "moreWidth must
   * not depend on the hidden count".
   */
  moreWidth: number
  /** Index of the active item, or -1 when nothing is active. */
  activeIndex: number
  /** Gap in px between two adjacent strip children. */
  gap: number
}

export interface FitResult {
  /** Indices rendered in the strip, in DOM order. */
  visible: number[]
  /** Indices moved into the disclosure, in DOM order. */
  hidden: number[]
}

/**
 * Width of a row of items, gaps included.
 *
 * Exported because `OverflowNav` needs the same number for the natural width it
 * publishes to `isCrowded`. Two copies of this formula that disagree would have
 * the fit and the crowding decision describing different rows.
 *
 * With the More button present there is one extra gap before it, so each item
 * costs `width + gap` uniformly once the button is reserved — which is what
 * makes the fill loops below a simple accumulation.
 */
export function rowWidth(widths: number[], gap: number): number {
  return widths.length === 0 ? 0 : sum(widths) + gap * (widths.length - 1)
}

/**
 * Which items stay in the strip, and which move into the More disclosure.
 *
 * The rules, in the order they are applied:
 *
 * 1. `available === undefined` — not measured yet (and the jsdom default):
 *    everything visible, nothing hidden, so no More button. This is the reason
 *    the unmeasured case is `undefined` and not `0`: a strip that has not
 *    reported a width yet must show every item, not collapse to one.
 * 2. everything fits — nothing hidden, and therefore no More button (the
 *    button is rendered iff `hidden.length > 0`; that is the only signal
 *    `FitResult` carries about it).
 * 3. something must hide — reserve `moreWidth` first, then fill from the
 *    start. Filling stops at the first item that does not fit rather than
 *    skipping it, so the visible run stays a contiguous prefix.
 * 4. the active item fell outside that prefix — reserve the active item too
 *    and re-fill from the start, which drops items from the END of the prefix
 *    until the active item fits. Not "drop one": a wide active item can cost
 *    several narrow ones.
 * 5. nothing fits at all — one item stays visible anyway, so the strip is
 *    never empty. The active one if there is one, otherwise the first.
 *
 * `available === 0` (measured, genuinely no room) needs no case of its own: it
 * falls out of 3 + 4/5 as "the active item only, plus the More button". That is
 * not a reachability guarantee — a zero-width strip clips the button too — it
 * only keeps the result well-formed.
 */
export function computeFit(input: FitInput): FitResult {
  const { itemWidths, available, moreWidth, activeIndex, gap } = input
  const count = itemWidths.length

  if (count === 0) return { visible: [], hidden: [] }

  const allVisible = (): FitResult => ({
    visible: itemWidths.map((_, i) => i),
    hidden: [],
  })

  // Rule 1: unmeasured means show everything.
  if (available === undefined) return allVisible()

  // Rule 2: everything fits, so no button.
  const naturalWidth = rowWidth(itemWidths, gap)
  if (naturalWidth <= available) return allVisible()

  // Rule 3: reserve the button, then fill from the start.
  let visible = fillFromStart(itemWidths, available, moreWidth, gap, -1)

  // Rule 4: the active item is not optional. Reserve it and re-fill, which
  // gives back a shorter prefix plus the active item, still in DOM order.
  const hasActive = activeIndex >= 0 && activeIndex < count
  if (hasActive && !visible.includes(activeIndex)) {
    const kept = fillFromStart(itemWidths, available, moreWidth, gap, activeIndex)
    visible = [...kept, activeIndex]
  }

  // Rule 5: never leave the strip empty.
  if (visible.length === 0) visible = [hasActive ? activeIndex : 0]

  const shown = new Set(visible)
  const hidden = itemWidths.map((_, i) => i).filter(i => !shown.has(i))
  return { visible, hidden }
}

/**
 * Fill a contiguous prefix of items into `available`, with the More button
 * always reserved and, when `reservedIndex >= 0`, that item reserved as well.
 *
 * Returns indices strictly below `reservedIndex` when one is given, so the
 * caller can append it and keep DOM order. It cannot run past a reserved
 * index: the prefix that failed to fit WITHOUT the reservation cannot fit with
 * it, so the loop always breaks first.
 */
function fillFromStart(
  itemWidths: number[],
  available: number,
  moreWidth: number,
  gap: number,
  reservedIndex: number,
): number[] {
  const limit = reservedIndex >= 0 ? reservedIndex : itemWidths.length
  let used = reservedIndex >= 0
    ? moreWidth + gap + itemWidths[reservedIndex]
    : moreWidth
  const kept: number[] = []
  for (let i = 0; i < limit; i++) {
    const next = used + gap + itemWidths[i]
    if (next > available) break
    used = next
    kept.push(i)
  }
  return kept
}

function sum(values: number[]): number {
  return values.reduce((total, v) => total + v, 0)
}

/**
 * How much crowding the collapsed state tolerates before it gives the chip back.
 *
 * This is the width of the hysteresis band, and 1px is the smallest value that
 * closes the whole sub-pixel error budget: the three quantities `isCrowded`
 * compares are rounded inconsistently (`available` comes from
 * `ResizeObserver`'s unrounded `contentRect.width`, the item widths and
 * `reserve` are each `Math.round`ed by their own measurement), so
 * `available - reserve` while collapsed reconstructs the expanded width only to
 * within ±0.5px. Anything smaller than the residual leaves rows that oscillate.
 *
 * A whole pixel is also below the smallest resize a pointer can produce, so the
 * band costs nothing an operator can see — the chip re-expands one pixel of
 * viewport later than the width at which it collapsed, and that is the only
 * observable difference.
 */
export const CROWD_HYSTERESIS_PX = 1

/**
 * Should the account chip collapse to an avatar?
 *
 * One physical threshold plus a one-pixel band, and the band is what makes the
 * answer stable. Collapsing the chip WIDENS the strip by `reserve`, so a
 * predicate written against the measured `available` alone would read a quantity
 * its own outcome changed: collapse frees room, the tabs then fit, the chip
 * expands, and it flips forever. Subtracting `reserve` while collapsed cancels
 * the width the collapse produced — but only to within the sub-pixel residual
 * described on `CROWD_HYSTERESIS_PX`, and a residual of the wrong sign is a
 * permanent flicker rather than a rounding error: the expanded form says
 * "crowded", the collapsed form says "not crowded", and neither state is a
 * fixed point.
 *
 * `CROWD_HYSTERESIS_PX` closes that by biasing the collapsed form toward staying
 * collapsed, which makes the predicate monotone in the state: whenever the
 * expanded form returns `true` the collapsed form does too. That is exactly the
 * condition for both states to be fixed points, so the flip has nowhere to
 * start. The expanded form is deliberately untouched, so the width at which the
 * chip collapses while a window narrows is unchanged; only the width at which it
 * comes back moves, by one pixel.
 *
 * `undefined` on either input means "not measured yet: stay expanded", so a
 * fresh narrow load settles one frame later rather than flickering. `reserve`
 * is a difference of the chip's two mirrored forms and is therefore known in
 * both states — without both mirrors this rule would deadlock the chip
 * expanded forever.
 */
export function isCrowded(args: {
  naturalWidth: number
  available: number | undefined
  collapsed: boolean
  reserve: number | undefined
}): boolean {
  const { naturalWidth, available, collapsed, reserve } = args
  if (available === undefined || reserve === undefined) return false
  const limit = collapsed ? available - reserve - CROWD_HYSTERESIS_PX : available
  return naturalWidth > limit
}
